/**
 * Contact-field validation for portal disbursement / partner forms.
 * Stricter than HTML5 type=email (browsers accept values like "ewfwefew").
 * Mirrors backend rules in routes/disbursementClient.js where applicable.
 */

import { validateEmail as validateEmailHelper } from './emailValidation';

export const CLIENT_CODE_REGEX = /^[A-Za-z0-9-]{1,20}$/;

export function validateEmail(email: string): string | null {
  const result = validateEmailHelper(email);
  if (!result.isValid) {
    return result.error || 'Enter a valid email address (e.g. finance@company.co.za)';
  }
  return null;
}

export function validateClientCode(code: string): string | null {
  const trimmed = String(code || '').trim();
  if (!trimmed) return 'Client code is required';
  if (!CLIENT_CODE_REGEX.test(trimmed)) {
    return 'Letters, numbers and hyphens only (max 20 characters)';
  }
  return null;
}

export function validateCompanyName(name: string): string | null {
  const trimmed = String(name || '').trim();
  if (!trimmed) return 'Company name is required';
  if (trimmed.length > 255) return 'Company name must be 255 characters or fewer';
  return null;
}

export function validateContactPhoneOptional(phone: string): string | null {
  const trimmed = String(phone || '').trim();
  if (!trimmed) return null;
  const compact = trimmed.replace(/\s/g, '');
  const digits = compact.replace(/\D/g, '');
  if (/^\+27[6-8]\d{8}$/.test(compact)) return null;
  if (/^27[6-8]\d{8}$/.test(digits)) return null;
  if (/^0[6-8]\d{8}$/.test(digits)) return null;
  return 'Enter a valid SA mobile number (e.g. +27 82 123 4567) or leave blank';
}

export function validateFloatLimitOptional(value: string): string | null {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) return 'Float limit must be zero or a positive number';
  return null;
}

export function validateWhiteLabelSlugOptional(slug: string): string | null {
  const trimmed = String(slug || '').trim();
  if (!trimmed) return null;
  if (!/^[a-z0-9-]{1,50}$/.test(trimmed.toLowerCase())) {
    return 'Slug: lowercase letters, numbers and hyphens only (max 50)';
  }
  return null;
}

export function validateRevenueSharePctOptional(enabled: boolean, pct: string): string | null {
  if (!enabled) return null;
  const trimmed = String(pct || '').trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0 || n > 100) return 'Revenue share must be between 0 and 100';
  return null;
}

export type DisbursementClientFormFields = {
  client_code: string;
  company_name: string;
  contact_email: string;
  contact_phone?: string;
  float_limit?: string;
  white_label_slug?: string;
  revenue_share_enabled?: boolean;
  revenue_share_pct?: string;
};

export type PartnerOnboardingCreateFormFields = {
  company_name: string;
  contact_email: string;
  contact_phone?: string;
  white_label_slug?: string;
  partner_type?: string;
  client_code?: string;
  revenue_share_enabled?: boolean;
  revenue_share_pct?: string;
  cpa_classification?: string;
  cpa_modes?: string[];
  cpa_services?: string[];
};

export function validatePartnerOnboardingCreateForm(
  form: PartnerOnboardingCreateFormFields,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const add = (field: string, message: string | null) => {
    if (message) errors[field] = message;
  };

  add('company_name', validateCompanyName(form.company_name));
  add('contact_email', validateEmail(form.contact_email));
  add('contact_phone', validateContactPhoneOptional(form.contact_phone || ''));

  const codeOverride = String(form.client_code || '').trim();
  if (codeOverride) {
    add('client_code', validateClientCode(codeOverride));
  }

  const slug = String(form.white_label_slug || '').trim();
  if (slug) {
    add('white_label_slug', validateWhiteLabelSlugOptional(slug));
  }

  add(
    'revenue_share_pct',
    validateRevenueSharePctOptional(!!form.revenue_share_enabled, form.revenue_share_pct || ''),
  );

  if (!form.cpa_classification || !(form.cpa_modes && form.cpa_modes.length) || !(form.cpa_services && form.cpa_services.length)) {
    errors.cpa_selections = 'Choose the partner classification, at least one integration mode, and at least one platform service.';
  }

  return errors;
}

export function validateDisbursementClientCreateForm(
  form: DisbursementClientFormFields,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const add = (field: string, message: string | null) => {
    if (message) errors[field] = message;
  };

  add('client_code', validateClientCode(form.client_code));
  add('company_name', validateCompanyName(form.company_name));
  add('contact_email', validateEmail(form.contact_email));
  add('contact_phone', validateContactPhoneOptional(form.contact_phone || ''));
  add('float_limit', validateFloatLimitOptional(form.float_limit || ''));
  add('white_label_slug', validateWhiteLabelSlugOptional(form.white_label_slug || ''));
  add(
    'revenue_share_pct',
    validateRevenueSharePctOptional(!!form.revenue_share_enabled, form.revenue_share_pct || ''),
  );

  return errors;
}

export function fieldInputClass(hasError: boolean, base = 'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2'): string {
  return `${base} ${hasError ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-green-200'}`;
}
